import { PlaneGeometry, RepeatWrapping, TextureLoader, Vector3 } from 'three';
import { Water } from 'three/examples/jsm/objects/Water.js';
import gsap from 'gsap';
import { Metrics } from '../_app/cuchillo/core/Metrics';
import { isPortrait } from '../_app/cuchillo/core/Basics';

// Normal map de ripples (portada de three; vegeu README del repo de referència).
// Es copia a /assets/images via el passthrough d'Eleventy (src/assets/images).
const WATER_NORMALS = '/assets/images/waternormals.jpg';

// Tot tunejable. Les mides van en unitats de MÓN; recorda que a camera2D 1u ≈ 1px a z=0,
// així que la mida del pla es deriva del rect del .__target (px) × spread.
// Resolució del pla (subdivisions). Constant fixa (ja no és un setting/animable).
const SEA_SEGMENTS = 245;

export const WATER_SETTINGS_DESKTOP = {
  // ── MIDA i POSICIÓ del pla ────────────────────────────────────────────────────
  spread: 8,           // mida del pla = mida del canvas (px) × spread. Més gran = mar que arriba
  //   més lluny (horitzó). No canvia la mida de les onades, només l'extensió.

  seaY: 0.055556,        // VERT (× HEIGHT, ref 900) · ≈50px @900 · alçada del mar (responsive)

  rotX: 10,               // rotació EXTRA en l'eix X (graus) sobre la base horitzontal (−90°). Inclina el pla.
  // ── COLOR (el porta el sistema d'hores; aquí només el valor per defecte) ────────
  waterColor: 0x010845,  // color base del mar = FOSC corporatiu (#010845). El sobreescriu TimeOfDay.
  sunColor: 0x7ab6f0,    // color del reflex especular (sol/lluna) = clar. El sobreescriu Scene.
  // ── ONADES GEOMÈTRIQUES (desplaçament real dels vèrtexs; injectat al vertex shader) ──
  waveAmplitude: 14,     // ALÇADA de les onades grans (unitats de món). 0 = pla pla. ↑ = onades altes.
  waveScale: 10,        // FREQÜÈNCIA de les onades grans: ↑ = onades més curtes/juntes (més steep),
  //   ↓ = onades més llargues/amples. Combinat amb amplitude dona la "steepness".
  waveSpeed: 0.6,        // velocitat del balanceig de les onades grans.
  waveChop: .04,           // TEMPESTA/SPIKINESS: 0 = onades rodones; ↑ = crestes punxegudes i pinçades
  //   (Gerstner: desplaça els vèrtexs horitzontalment cap a les crestes) +
  //   detall d'alta freqüència → onades "fortes" de tempesta. Independent de l'alçada.
  // ── NOISE / RIPPLES (normal map animat del Water natiu; el detall de la superfície) ──
  noiseSize: 30,        // MIDA del noise de la superfície: ↑ = ones de noise MÉS GRANS (menys tiling).
  distortionScale: 7.9,  // FORÇA del noise: com de fort distorsiona el reflex (arrissat/choppy).
  timeSpeed: 0.0024,     // velocitat de l'animació del noise.
  // ── REFLEXOS: MÉS LLUMS i MÉS OMBRES / matisos (patch al fragment del Water) ──
  //   Donen varietat de LLUMINOSITAT → el grade duotone ho tradueix en més matisos de color.
  reflectionBoost: 0.55,   // reflex EXTRA a angle rasant (Fresnel) → més LLUMS a la llunyania/horitzó.
  fresnelPower: 1.8,       // com de "rasant" és aquest reflex (↑ = llums només molt cap a l'horitzó).
  specularStrength: 3.4,   // força de les ESPURNES del sol a les cares de les onades → llums nítides.
  waveShade: 0.75,         // OMBRA de les onades grans: crestes CLARES / valls FOSQUES (llums+ombres).
  shadingContrast: 1.15,   // contrast final del mar: aprofundeix ombres i puja llums → més matisos.
  // ── Tècnics ────────────────────────────────────────────────────────────────────
  textureSize: 512,      // resolució del render target de reflexió.
  alpha: 1.0,            // opacitat base del mar.
};

export const WATER_SETTINGS_PORTRAIT = {
  ...WATER_SETTINGS_DESKTOP,

  // aquí només sobreescrius els que canviïn.
  // seaY: fracció × HEIGHT. A tablet HEIGHT és gran → un valor negatiu com -1.12 deixa el mar MOLT avall
  // (fora de quadre). El repòs ha de quedar FRONTAL (prop de 0, com desktop) perquè es vegi.
  seaY: -0.1,     // repòs frontal a mobile (fracció × HEIGHT)
  rotX: 13,       // inclinació a mobile
};

// Actiu segons orientació (com SEA_SCROLL): a portrait s'apliquen els overrides de _PORTRAIT.
export const WATER_SETTINGS = isPortrait ? { ...WATER_SETTINGS_PORTRAIT } : { ...WATER_SETTINGS_DESKTOP };

export const SEA_INTRO_FROM_DESKTOP = {
  seaY: -1.2,
  rotX: 4,
};

export const SEA_INTRO_FROM_PORTRAIT = {
  seaY: -1.2,   // sota el frame a l'inici (com desktop) → puja fins al repòs frontal i entra en quadre
  rotX: 3,
};

// Estat INICIAL de l'entrada, actiu segons orientació.
export const SEA_INTRO_FROM = isPortrait ? { ...SEA_INTRO_FROM_PORTRAIT } : { ...SEA_INTRO_FROM_DESKTOP };

// Estat FINAL (repòs) de l'entrada = snapshot dels valors de repòs de WATER_SETTINGS (mateixes claus que
// SEA_INTRO_FROM), pres al càrrega → referència ESTABLE per al botó "set sea FINAL" encara que
// WATER_SETTINGS es muti en runtime (animació / botons).
// export const SEA_INTRO_TO = Object.fromEntries(Object.keys(SEA_INTRO_FROM).map((k) => [k, WATER_SETTINGS[k]]));

// El WaterPlane és un "item" del nostre sistema (init/loop/resize/show/hide/dispose), però
// NO estén WebGLObject: el Water natiu de three ÉS una subclasse de Mesh, és horitzontal i es
// dimensiona en unitats de món (no és un billboard vertical mapat 1:1 al DOM). Per tant l'embolcallem
// amb el mateix vocabulari de cicle de vida, i fem servir el .__target NOMÉS per definir-ne la mida.
export default class WaterPlane {
  constructor(dom) {
    this.dom = dom;               // el .__target: en llegim el rect per dimensionar el pla
    this.water = null;
    this._opacityProxy = { o: 0 };
    this._sunDir = new Vector3(0, 0.2, -1).normalize();
    this._builtSpread = null;
    this._scrollY = 0;
  }

  init() {
    const { width, height } = this._targetSize();

    const normals = new TextureLoader().load(WATER_NORMALS, (t) => {
      t.wrapS = t.wrapT = RepeatWrapping;
    });

    const seg = SEA_SEGMENTS;
    const geometry = new PlaneGeometry(width, height, seg, seg);
    this.water = new Water(geometry, {
      textureWidth: WATER_SETTINGS.textureSize,
      textureHeight: WATER_SETTINGS.textureSize,
      waterNormals: normals,
      sunDirection: this._sunDir.clone(),
      sunColor: WATER_SETTINGS.sunColor,
      waterColor: WATER_SETTINGS.waterColor,
      distortionScale: WATER_SETTINGS.distortionScale,
      alpha: WATER_SETTINGS.alpha,
      fog: false,
    });

    // Onades geomètriques: parxegem el vertex shader del Water natiu (pla) per desplaçar els
    // vèrtexs en vertical (local Z, que amb la rotació −90° esdevé l'alçada del món). Guardem
    // els uniforms per poder tunejar amplitud/mida/velocitat en viu.
    this._patchWaves();

    // Horitzontal (mar) i a l'alçada del mar. El mar és ambiental: se centra sota la càmera,
    // no es mapeja a la posició X/Y del DOM (el .__target només en fixa la mida).
    this.water.rotation.x = -Math.PI / 2 + WATER_SETTINGS.rotX * Math.PI / 180;
    this.water.position.set(0, WATER_SETTINGS.seaY * Metrics.HEIGHT, 0); // seaY = fracció × HEIGHT (responsive)

    // Mida del noise de la superfície (uniform `size` del Water: ↑ size = més tiling = ripples més
    // petits). L'exposem com a noiseSize intuïtiu (↑ = més grans) → size = 1 / noiseSize.
    this.water.material.uniforms.size.value = 1 / Math.max(0.05, WATER_SETTINGS.noiseSize);

    // Estat inicial amagat (el show el fa fade-in)
    this.water.material.uniforms.alpha.value = 0;
    this.water.visible = false;
    this._builtSpread = WATER_SETTINGS.spread;

    return this.water;
  }

  // Injecta onades geomètriques al vertex shader del Water (que és PLA). Desplaça `position` en
  // local Z (→ alçada de món) amb una suma de sinus; reutilitza el uniform `time` del Water.
  _patchWaves() {
    const mat = this.water.material;
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uWaveAmp = { value: WATER_SETTINGS.waveAmplitude };
      shader.uniforms.uWaveScale = { value: WATER_SETTINGS.waveScale };
      shader.uniforms.uWaveSpeed = { value: WATER_SETTINGS.waveSpeed };
      shader.uniforms.uWaveChop = { value: WATER_SETTINGS.waveChop };
      // Reflexos enriquits (més llums/ombres/matisos).
      shader.uniforms.uReflBoost = { value: WATER_SETTINGS.reflectionBoost };
      shader.uniforms.uFresnelPow = { value: WATER_SETTINGS.fresnelPower };
      shader.uniforms.uSpecStrength = { value: WATER_SETTINGS.specularStrength };
      shader.uniforms.uWaveShade = { value: WATER_SETTINGS.waveShade };
      shader.uniforms.uShadingContrast = { value: WATER_SETTINGS.shadingContrast };
      this._waveUniforms = shader.uniforms;
      shader.vertexShader = shader.vertexShader
        .replace(
          'uniform float time;',
          `uniform float time;
           uniform float uWaveAmp;
           uniform float uWaveScale;
           uniform float uWaveSpeed;
           uniform float uWaveChop;
           varying float vWaveShade; // alçada normalitzada de l'onada → ombra al fragment
           // Desplaçament de l'onada en LOCAL (x, y = horitzontal del pla; z = alçada de món).
           // Dues onades direccionals (X i Y). uWaveChop (0..1) hi afegeix la "tempesta":
           //  · Gerstner: desplaça els vèrtexs cap a les crestes (cos) → crestes punxegudes, valls planes.
           //    El factor 0.92/k manté S·k < 1 → sense auto-interseccions (estable a qualsevol segments).
           //  · harmònics d'alta freqüència → detall choppy de tempesta.
           vec3 _waveOffset(vec3 p) {
             float f = uWaveScale * 0.00035;
             float t = time * uWaveSpeed;
             float ph1 = p.x * f + t;
             float ph2 = p.y * (f * 0.85) + t * 1.3;
             float a1 = uWaveAmp * 0.5;
             float a2 = uWaveAmp * 0.5 * 0.8;
             // Alçada base (idèntica a l'original quan chop = 0) + detall d'alta freqüència amb chop.
             float z = a1 * sin(ph1) + a2 * sin(ph2);
             z += uWaveChop * (a1 * 0.30 * sin(ph1 * 2.7 + t * 1.7)
                             + a2 * 0.30 * sin(ph2 * 3.1 - t * 1.9));
             // Pinçament horitzontal (Gerstner): capat sota el llindar de bucle per estabilitat.
             float S1 = (uWaveChop * 0.92) / max(f, 1e-6);
             float S2 = (uWaveChop * 0.92) / max(f * 0.85, 1e-6);
             float x = S1 * cos(ph1);
             float y = S2 * cos(ph2);
             return vec3(x, y, z);
           }`
        )
        // Calcula l'onada UN cop a l'inici de main i desa'n l'alçada normalitzada per al fragment.
        .replace('void main() {',
          'void main() {\n vec3 _wo = _waveOffset( position );\n vWaveShade = clamp(_wo.z / max(uWaveAmp, 1.0), -1.0, 1.0);')
        // El vertex shader del Water fa servir `vec4( position, 1.0 )` (mirrorCoord + mvPosition);
        // el substituïm per la posició desplaçada perquè geometria i reflex coincideixin.
        .replace(/vec4\(\s*position,\s*1\.0\s*\)/g,
          'vec4( position + _wo, 1.0 )');

      // ── FRAGMENT: més LLUMS i OMBRES / matisos als reflexos. ────────────────────────
      shader.fragmentShader = shader.fragmentShader
        // Uniforms + varying de l'onada (sunColor és exclusiu del fragment del Water).
        .replace('uniform vec3 sunColor;',
          `uniform vec3 sunColor;
           uniform float uReflBoost, uFresnelPow, uSpecStrength, uWaveShade, uShadingContrast;
           varying float vWaveShade;`)
        // Enriqueix el color de sortida: Fresnel (llums rasants) + espurnes del sol + ombra
        // d'onada (crestes clares / valls fosques) + contrast (aprofundeix ombres, puja llums).
        .replace('vec3 outgoingLight = albedo;',
          `vec3 outgoingLight = albedo;
           float _fres = pow(1.0 - clamp(theta, 0.0, 1.0), uFresnelPow);
           outgoingLight += uReflBoost * _fres * reflectionSample;      // més LLUMS a l'horitzó
           outgoingLight += sunColor * specularLight * uSpecStrength;   // espurnes del sol
           outgoingLight *= (1.0 + uWaveShade * vWaveShade);            // crestes clares / valls fosques
           outgoingLight = max(vec3(0.0), (outgoingLight - 0.35) * uShadingContrast + 0.35);`);
    };
    mat.needsUpdate = true;
  }

  // Mida del pla derivada del .__target (px → món) × spread.
  _targetSize() {
    const rect = this.dom?.getBoundingClientRect();
    const w = (rect?.width || 720) * WATER_SETTINGS.spread;
    const h = (rect?.height || 720) * WATER_SETTINGS.spread;
    return { width: w, height: h };
  }

  // Direcció del sol/lluna (per al reflex especular sobre el mar). La marca Scene.
  setSunDirection(vec3) {
    this._sunDir.copy(vec3).normalize();
    if (this.water) this.water.material.uniforms.sunDirection.value.copy(this._sunDir);
  }

  // Colors del mar i del reflex especular (els alimenta el sistema d'hores). Reben THREE.Color.
  setColors(waterColor, sunColor) {
    if (!this.water) return;
    if (waterColor) this.water.material.uniforms.waterColor.value.copy(waterColor);
    if (sunColor) this.water.material.uniforms.sunColor.value.copy(sunColor);
  }

  // Aplica settings del mar en viu (debug). distortionScale i seaY són immediats; timeSpeed el
  // llegeix el loop directament; spread refà la geometria (mida del pla).
  setConfig(partial = {}) {
    Object.assign(WATER_SETTINGS, partial);
    if (this.water) {
      this.water.material.uniforms.distortionScale.value = WATER_SETTINGS.distortionScale;
      this.water.material.uniforms.size.value = 1 / Math.max(0.05, WATER_SETTINGS.noiseSize);
      this.water.position.y = WATER_SETTINGS.seaY * Metrics.HEIGHT; // fracció × HEIGHT (responsive)
      this.water.rotation.x = -Math.PI / 2 + WATER_SETTINGS.rotX * Math.PI / 180;
    }
    if (this._waveUniforms) {
      this._waveUniforms.uWaveAmp.value = WATER_SETTINGS.waveAmplitude;
      this._waveUniforms.uWaveScale.value = WATER_SETTINGS.waveScale;
      this._waveUniforms.uWaveSpeed.value = WATER_SETTINGS.waveSpeed;
      this._waveUniforms.uWaveChop.value = WATER_SETTINGS.waveChop;
      this._waveUniforms.uReflBoost.value = WATER_SETTINGS.reflectionBoost;
      this._waveUniforms.uFresnelPow.value = WATER_SETTINGS.fresnelPower;
      this._waveUniforms.uSpecStrength.value = WATER_SETTINGS.specularStrength;
      this._waveUniforms.uWaveShade.value = WATER_SETTINGS.waveShade;
      this._waveUniforms.uShadingContrast.value = WATER_SETTINGS.shadingContrast;
    }
    // Refà el pla si ha canviat el spread (mida). Els segments són constants (SEA_SEGMENTS).
    if (WATER_SETTINGS.spread !== this._builtSpread) this.resize();
  }

  show(duration = 2, delay = 0) {
    if (!this.water) return;
    this.water.visible = true;
    gsap.killTweensOf(this._opacityProxy);
    this._opacityProxy.o = this.water.material.uniforms.alpha.value;
    gsap.to(this._opacityProxy, {
      o: WATER_SETTINGS.alpha, duration, delay, ease: 'power2.out',
      onUpdate: () => { this.water.material.uniforms.alpha.value = this._opacityProxy.o; },
    });
  }

  hide(duration = 1, delay = 0) {
    if (!this.water) return;
    gsap.killTweensOf(this._opacityProxy);
    this._opacityProxy.o = this.water.material.uniforms.alpha.value;
    gsap.to(this._opacityProxy, {
      o: 0, duration, delay, ease: 'power2.out',
      onUpdate: () => { this.water.material.uniforms.alpha.value = this._opacityProxy.o; },
      onComplete: () => { this.water.visible = false; },
    });
  }

  // Offset additiu de l'alçada del mar pel MOVE del billboard (0 = sense moviment). Se suma a seaY.
  setScrollY(v) { this._scrollY = v || 0; }

  // Anima els ripples. dt opcional (per si un dia lliguem la velocitat al delta real).
  loop() {
    if (!this.water) return;
    this.water.material.uniforms.time.value += WATER_SETTINGS.timeSpeed;
    // Alçada = (seaY base, animat per l'intro + offset del move/scroll) × HEIGHT (responsive; tot en fracció).
    this.water.position.y = (WATER_SETTINGS.seaY + (this._scrollY || 0)) * Metrics.HEIGHT;
  }

  // Re-dimensiona el pla segons el .__target actual (canvi de mida de finestra).
  resize() {
    if (!this.water) return;
    const { width, height } = this._targetSize();
    const seg = SEA_SEGMENTS;
    this.water.geometry.dispose();
    this.water.geometry = new PlaneGeometry(width, height, seg, seg);
    this._builtSpread = WATER_SETTINGS.spread;
  }

  get settings() { return WATER_SETTINGS; }

  dispose() {
    gsap.killTweensOf(this._opacityProxy);
    if (this.water) {
      this.water.geometry?.dispose();
      this.water.material?.dispose();
      this.water = null;
    }
  }
}
